import {
  useGetCurrentUserQuery,
  useLazyGetDemoSearchLinksResultsQuery,
  useRestoreDemoMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  MoreVerticalOutlineIcon,
  OpenInNew1OutlineIcon,
  PlayOutlineIcon,
  RightOutlineIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import {
  type IDemo,
  PermissionAction,
  PermissionKey,
  RecordingTypes,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  InlineLink,
  Separator,
  SubMenu,
  TextCaption,
  Tooltip,
  notify,
} from '@carboncopyco/ui';
import { isDesktop, useDemoAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactNode, isValidElement, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIsDemoLocked } from '../../../../../hooks';
import {
  ArchiveDemoModal,
  CopyDemoModalWithFolders,
  DuplicateDemoModal,
  MoveDemoModal,
} from '../../../../AssetActions/Demo';
import { AssetLockedModal } from '../../../../AssetLockedModal';
import { DemoLinkModal } from '../../../../DemoLinkModal';
import { GiveADemoModal } from '../../../../PresentationModals';
import { ShareDemoFlow } from '../../../../Share';
import { useDesktopDeeplink, usePermissionsContext } from '../../../../utils';
import { useLinksPermissions } from '../../../../utils/hooks/useLinksPermissions';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { ArchiveTileDropdown } from '../ArchiveTileDropdown';

interface DemoActionsDropdownProps {
  demo: IDemo;
  archived?: boolean;
  inFolder?: boolean;
  handleMenuOpen: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
}
export const DemoActionsDropdown = ({
  demo,
  archived,
  inFolder,
  handleMenuOpen,
  triggerElement,
}: DemoActionsDropdownProps) => {
  const navigate = useNavigate();
  const flags = useFlags();
  const [isMenuOpen, { toggle: toggleMenu }] = useBoolean(false);
  const archiveMessageRef = useRef<HTMLDivElement>(null);
  const [showGiveADemoModal, setShowGiveADemoModal] = useBoolean();
  const [showDuplicateDemoModal, setShowDuplicateDemoModal] = useBoolean();
  const [showArchiveDemoModal, setShowArchiveDemoModal] = useBoolean();
  const [showDemoLinkModal, setShowDemoLinkModal] = useState(false);

  const {
    organizationId,
    workspaceId,
    goToEditor,
    goToDemoPreview,
    goToGiveADemoPreview,
    goToDemoShares,
    rootPath,
    previewArchivedDemo,
    handleCreatePresentation,
    playPresentation,
    desktopDomain,
  } = useWorkspacePageProps();
  const [getLinkResponse, { data: linksResponse }] =
    useLazyGetDemoSearchLinksResultsQuery();
  const {
    trackDemoClickDuplicate,
    trackDemoClickEdit,
    trackDemoClickCopyToWorkspace,
    trackDemoClickShare,
    trackDemoClickArchive,
    trackDemoClickPresent,
    trackDemoClickPreview,
    trackDemoMultiUserLock,
  } = useDemoAnalytics(
    inFolder ? 'folder_page_demo_tile' : 'workspace_page_demo_tile'
  );
  const { data: userData } = useGetCurrentUserQuery();
  const userRole = userData?.user?.roles?.[organizationId];

  const { getPermissions } = usePermissionsContext();

  const handlePreviewDemo = () => {
    goToDemoPreview({
      id: demo.id,
      firstUrl: demo.firstUrl,
    });
  };
  const { getIsDemoShareable } = useLinksPermissions();
  const { exeDeeplinkWithAction, shouldShowDeeplink } = useDesktopDeeplink();

  const [restoreDemo] = useRestoreDemoMutation();

  const duplicateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.duplicateDemo
  );
  const copyToWorkspacePermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.copyDemo
  );
  const editDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );
  const deleteDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.deleteDemoById
  );
  const moveDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.moveDemo
  );

  const [isDemoLockedModal, setIsDemoLockedModal] = useState(false);

  const { checkIsDemoLocked, userName } = useIsDemoLocked({
    demoId: String(demo.id),
    organizationId,
    currentUserEmail: userData.user.email,
    shouldRunInterval: false,
  });

  const handleEditDemo = () => {
    trackDemoClickEdit({ ...demo });
    exeDeeplinkWithAction(
      {
        action: 'edit',
        assetId: demo.id.toString(),
        organizationId: organizationId,
        workspaceId,
        assetType: 'demo',
      },
      () => goToEditor(demo.id, demo.recordingType)
    );
  };

  const showShareDemoFlow = () => {
    NiceModal.show(ShareDemoFlow, {
      workspaceId,
      demoId: Number(demo.id),
      goToShares: () => goToDemoShares(Number(demo.id)),
    });
  };

  const isMobileClone = demo.recordingType === RecordingTypes.mobile;
  const isEditMobileDisabled = isMobileClone && !flags.editMobileCloneDemo;
  const allowEdit =
    !demo.readOnly && editDemoPermission && !isEditMobileDisabled;

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {archived ? (
        <ArchiveTileDropdown
          inFolder={inFolder}
          restore={() => {
            restoreDemo({
              id: String(demo.id),
              organizationId,
            })
              .unwrap()
              .then(() => {
                notify.success(
                  `Demo "${demo.demoName}" was successfully restored`
                );
              })
              .catch(() => {
                notify.error(`Error restoring demo "${demo.demoName}"`);
              });
          }}
          preview={() => {
            toggleMenu();
            previewArchivedDemo(demo);
          }}
          open={isMenuOpen}
          toggleOpen={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
        />
      ) : (
        <Dropdown
          asChild
          width={200}
          open={isMenuOpen}
          onOpenChange={() => {
            handleMenuOpen(!isMenuOpen);
            !isMenuOpen &&
              getLinkResponse({
                workspaces: [workspaceId],
                organizationId,
                id: String(demo.id),
                active: true,
              });
            toggleMenu();
          }}
          triggerElement={
            isValidElement(triggerElement) ? (
              triggerElement
            ) : (
              <Button
                aria-label="demo tile actions"
                small
                dark
                icon={<MoreVerticalOutlineIcon />}
              />
            )
          }
        >
          <div className="p-2 flex flex-col gap-2">
            <div>
              <DropdownItem
                iconLeft={<ShowOutlineIcon size={18} />}
                label="Preview"
                onClick={(event) => {
                  event.preventDefault();
                  toggleMenu();
                  trackDemoClickPreview(demo);
                  handlePreviewDemo();
                }}
              />

              <DropdownItem
                iconLeft={<PlayOutlineIcon />}
                label="Give a demo"
                onClick={() => {
                  trackDemoClickPresent({ ...demo });
                  if (isDesktop()) {
                    handleCreatePresentation?.(demo);
                    return;
                  }
                  setShowGiveADemoModal.setTrue();
                }}
              />

              {allowEdit && (
                <DropdownItem
                  iconLeft={<EditIcon size={18} />}
                  iconRight={
                    shouldShowDeeplink ? (
                      <OpenInNew1OutlineIcon size={15} />
                    ) : null
                  }
                  label="Edit"
                  onClick={async (event) => {
                    event.preventDefault();
                    toggleMenu();

                    if (flags.editorMultiUserLocking) {
                      const lockedBy = await checkIsDemoLocked();
                      if (lockedBy) {
                        setIsDemoLockedModal(true);

                        trackDemoMultiUserLock({
                          variables: {
                            userEditing: lockedBy,
                            userTryingToEdit: userData.user.email,
                          },
                          demoId: demo.id,
                        });

                        return;
                      }
                    }

                    handleEditDemo();
                  }}
                />
              )}
              {!demo.readOnly && duplicateDemoPermission && (
                <DropdownItem
                  iconLeft={<DuplicateOutlineIcon size={18} />}
                  label="Duplicate"
                  onClick={() => {
                    trackDemoClickDuplicate({ ...demo });
                    setShowDuplicateDemoModal.setTrue();
                  }}
                />
              )}
              {!demo.readOnly && moveDemoPermission && (
                <DropdownItem
                  iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
                  label="Move to..."
                  onClick={() => {
                    NiceModal.show(MoveDemoModal, {
                      organizationId,
                      demo,
                      workspaceId,
                    });
                  }}
                />
              )}
              {!demo.readOnly && copyToWorkspacePermission && (
                <DropdownItem
                  iconLeft={<CopyOutlineIcon size={18} />}
                  label="Copy to..."
                  onClick={() => {
                    trackDemoClickCopyToWorkspace({
                      ...demo,
                    });
                    NiceModal.show(CopyDemoModalWithFolders, {
                      demo,
                      organizationId,
                    });
                  }}
                />
              )}
              {getIsDemoShareable(demo) && (
                <>
                  {demo?.readOnly ? (
                    <DropdownItem
                      label="Share"
                      iconLeft={<ShareOutlineIcon size={18} />}
                      onClick={() => {
                        trackDemoClickShare({ ...demo });
                        showShareDemoFlow();
                      }}
                    />
                  ) : (
                    <>
                      <Separator horizontal />
                      <SubMenu
                        width={205}
                        triggerProps={{
                          label: 'Share',
                          iconRight: <RightOutlineIcon />,
                          iconLeft: <ShareOutlineIcon size={18} />,
                        }}
                      >
                        <div>
                          <DropdownItem
                            label="Share internally"
                            onClick={() => {
                              setShowDemoLinkModal(true);
                            }}
                            disabled={demo.readOnly}
                          />
                        </div>

                        <div>
                          <DropdownItem
                            label="Share externally"
                            onClick={showShareDemoFlow}
                          />
                        </div>
                      </SubMenu>
                      <Separator horizontal />
                    </>
                  )}
                </>
              )}
              {!demo.readOnly && deleteDemoPermission && (
                <>
                  <div
                    ref={archiveMessageRef}
                    className="disabled:pointer-events-none"
                  >
                    <DropdownItem
                      disabled={
                        !linksResponse || !!linksResponse?.metadata?.totalItems
                      }
                      iconLeft={<ArchiveIcon size={20} />}
                      label="Archive"
                      onClick={() => {
                        trackDemoClickArchive({ ...demo });
                        setShowArchiveDemoModal.setTrue();
                      }}
                    />
                  </div>
                  {!!linksResponse?.metadata?.totalItems && (
                    <Tooltip
                      anchorRef={archiveMessageRef}
                      placement="bottom"
                      offset={[0, 20]}
                    >
                      <div className="flex flex-col items-start p-2.5">
                        <TextCaption
                          label="To archive this demo, please deactivate all of its shared links first."
                          color="text-ds-natural-600"
                        />
                        <InlineLink
                          as={Link}
                          to={`${rootPath}/demo/${demo.id}/links`}
                          label="View shares"
                        />
                      </div>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </div>
        </Dropdown>
      )}
      {showGiveADemoModal && (
        <GiveADemoModal
          workspaceId={workspaceId}
          onClose={setShowGiveADemoModal.setFalse}
          open={showGiveADemoModal}
          demoId={String(demo.id)}
          organizationId={organizationId}
          goToDemoPreview={goToGiveADemoPreview}
          goToPresentationPage={playPresentation}
          goToDemoPresentations={() => {
            navigate(`${rootPath}/demo/${demo.id}/presentations`);
          }}
          desktopDomain={desktopDomain}
        />
      )}
      {showDuplicateDemoModal && (
        <DuplicateDemoModal
          organizationId={organizationId}
          demoId={String(demo.id)}
          onClose={setShowDuplicateDemoModal.setFalse}
          show={showDuplicateDemoModal}
          folderId={String(demo.folderId)}
        />
      )}
      {showArchiveDemoModal && (
        <ArchiveDemoModal
          folderId={String(demo.folderId)}
          organizationId={organizationId}
          demoId={String(demo.id)}
          show={showArchiveDemoModal}
          onClose={setShowArchiveDemoModal.setFalse}
        />
      )}
      {showDemoLinkModal && (
        <DemoLinkModal
          workspaceId={workspaceId}
          assetId={String(demo.id)}
          assetType="demo"
          organizationId={organizationId}
          isOpen={showDemoLinkModal}
          onClose={() => {
            setShowDemoLinkModal(false);
          }}
          role={userRole}
        />
      )}
      {isDemoLockedModal && (
        <AssetLockedModal
          userName={userName}
          buttonLabel="Okay"
          onButtonClick={() => {
            setIsDemoLockedModal(false);
          }}
        />
      )}
    </div>
  );
};
